import { yupResolver } from "@hookform/resolvers/yup";
import { iArea } from "core/kangarooTypes/iArea";
import { iCity } from "core/kangarooTypes/iCity";
import { ChevronDown, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Button from "shared/Button";
import { Dialog } from "shared/Dialog/Dialog";
import { DialogContent } from "shared/Dialog/DialogContent";
import { DialogFooter } from "shared/Dialog/DialogFooter";
import { DialogHeader } from "shared/Dialog/DialogHeader";
import { DialogTitle } from "shared/Dialog/DialogTitle";
import FormInput from "shared/FormInput";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

// Props for the AreaFormDialog component
interface AreaFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AreaFormData) => void;
  initialData?: iArea;
  isEditing: boolean;
  cities: iCity[];
}

// Area Form Dialog Component
const AreaFormDialog = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isEditing,
  cities,
}: AreaFormDialogProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form validation schema for areas
  const AreaFormSchema = yup.object({
    id: yup.number(),
    cityId: yup.number().required(t("areaFormDialog.validation.cityRequired")),
    name: yup.string().required(t("areaFormDialog.validation.areaNameRequired")),
    price: yup
      .number()
      .required(t("areaFormDialog.validation.deliveryPriceRequired"))
      .min(0, t("areaFormDialog.validation.pricePositive")),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AreaFormData>({
    resolver: yupResolver(AreaFormSchema),
    defaultValues: {
      id: 0,
      cityId: cities.length > 0 ? cities[0].id : 0,
      name: "",
      price: 0,
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset(
        initialData || {
          id: 0,
          cityId: cities.length > 0 ? cities[0].id : 0,
          name: "",
          price: 0,
        }
      );
    }
  }, [initialData, reset, isOpen, cities]);

  const handleFormSubmit = async (data: AreaFormData) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data);
      handleClose();
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    reset({
      id: 0,
      cityId: cities.length > 0 ? cities[0].id : 0,
      name: "",
      price: 0,
    });
  };

  const cityOptions = cities.map((city) => ({
    value: city.id,
    label: city.name,
  }));

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex justify-between items-center">
            <span>
              {isEditing
                ? t("areaFormDialog.title.edit")
                : t("areaFormDialog.title.add")}
            </span>
            <button
              onClick={handleClose}
              className="rounded-full p-1 hover:bg-gray-100 transition-colors"
            >
              <X size={18} />
            </button>
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 flex flex-col justify-start h-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label={t("areaFormDialog.form.city")}
              name="cityId"
              type="select"
              register={register}
              errors={errors}
              selectOptions={cityOptions}
              icon={<ChevronDown size={20} />}
              placeholder={t("areaFormDialog.form.selectCity")}
            />
            <FormInput
              label={t("areaFormDialog.form.areaName")}
              name="name"
              register={register}
              errors={errors}
              placeholder={t("areaFormDialog.form.enterAreaName")}
            />
            <FormInput
              label={t("areaFormDialog.form.deliveryPrice")}
              name="price"
              type="number"
              register={register}
              errors={errors}
              placeholder={t("areaFormDialog.form.enterDeliveryPrice")}
            />
          </div>
        </div>

        <DialogFooter className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t("areaFormDialog.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(handleFormSubmit)}
            isLoading={isSubmitting}
          >
            {isEditing
              ? t("areaFormDialog.buttons.update")
              : t("areaFormDialog.buttons.add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AreaFormDialog;

export type AreaFormData = {
  id?: number;
  cityId: number;
  name: string;
  price: number;
};
